import React from "react";
import { I18n } from "react-redux-i18n";

import { getTestAttributes } from "@common/utils/tests";
import { Button } from "@common/components/Button";
import { Flex } from "@common/components/Flex";
import { Loading } from "@common/components/loading";
import { HttpErrorDto } from "@common/dto/http";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import Tree, { TreeGetIdArgs, getItem } from "@common/components/Tree";
import { Checkbox } from "@common/components/checkbox";
import { MODULE_NAME } from "@modules/pedigrees/constants/shared";
import { Modal, ModalBody, ModalHeader, ModalTitle } from "@common/components/modal";
import { FormControl } from "@common/components/form-control";

import "./create-shortcut.styles.scss";

export type CreateShortcutProps = {
  folderId?: FolderPrimaryListDto['id'];
  pedigreeName: string;
  onClose: () => void;
  getFolders: () => Promise<Array<FolderPrimaryListDto>>;
  showErrorMessage: (error: string) => void;
  onSubmit: (folder: FolderPrimaryListDto, pedigreeName: string, openOnSave: boolean) => Promise<void>;
};

export const CreateShortcut: React.FC<CreateShortcutProps> = (props) => {
  const { onClose, onSubmit, showErrorMessage, getFolders, pedigreeName: fromPedigreeName, folderId } = props;
  const [submitting, setSubmitting] = React.useState(false);
  const [initializing, setInitializingState] = React.useState(true);
  const [openOnSave, setOpenOnSaveState] = React.useState(false);
  const [pedigreeName, setPedigreeName] = React.useState(`Shortcut to ${fromPedigreeName}`);
  const [folders, setFolders] = React.useState<Array<FolderPrimaryListDto>>([]);
  const [selectedFolder, setSelectedFolder] = React.useState<FolderPrimaryListDto | undefined>();

  React.useEffect(() => {
    setInitializingState(true);
    getFolders()
      .then((data) => {
        const defaultSelectedFolder = getItem(folderId, data);
        setSelectedFolder(defaultSelectedFolder?.id != null ? defaultSelectedFolder : undefined);
        setFolders(data);
      })
      .catch((err) => {
        showErrorMessage(err?.entity || I18n.t("Failed to get folders"));
        console.error(err);
      })
      .finally(() => {
        setInitializingState(false);
      });
  }, []);

  const handleSubmit = React.useCallback(async () => {
    if (!selectedFolder) {
      return;
    }
    
    setSubmitting(true);

    try {
      await onSubmit(selectedFolder, pedigreeName, openOnSave);
    } catch (err) {
      console.error(err);
      setSubmitting(false);
      showErrorMessage((err as HttpErrorDto)?.entity || I18n.t("Failed to create shortcut"));
    }
  }, [onSubmit, showErrorMessage, selectedFolder, pedigreeName, openOnSave]);

  const handleFolderChange = React.useCallback((id: FolderPrimaryListDto['id'], folder: FolderPrimaryListDto) => {
    if (id === selectedFolder?.id) {
      return;
    }

    setSelectedFolder(folder);
  }, [selectedFolder]);

  const handlePedigreeNameChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPedigreeName(e.target.value);
  }, []);

  const getFolderId = React.useMemo(() => {
    return {
      folder: (params: TreeGetIdArgs) => `tree-root-child${params.parentIndex || 0}-child${params.ownIndex}`,
      collapseIcon: (params: TreeGetIdArgs) => `tree-root-child${params.parentIndex || 0}-child${params.ownIndex}-image`,
    };
  }, []);

  return (
    <Modal
      show
      animation={false}
      bsSize="medium"
      backdrop="static"
      bsClass="bootstrap modal"
      className="PedigreesCreateShortcut"
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle
          {...getTestAttributes(MODULE_NAME, 'create-shortcut', 'modal-title')}
        >
          {I18n.t("Create Shortcut")}
        </ModalTitle>
      </ModalHeader>

      <ModalBody>
        {initializing 
          ? <Flex className="PedigreesCreateShortcut__loading" justify="center" alignItems="center"><Loading size={30} /></Flex>
          : (
            <>
              <Flex alignItems="center">
                <span>{I18n.t("Pedigree Name")}</span>
                <FormControl
                  className="PedigreesCreateShortcut__name-input"
                  value={pedigreeName}
                  {...getTestAttributes({ id: 'tx-ped-shcut-name' })}
                  onChange={handlePedigreeNameChange}
                />
                <Checkbox
                  checked={openOnSave}
                  className="PedigreesCreateShortcut__open-checkbox"
                  {...getTestAttributes({ id: 'cbx-ped-shcut-open-input' })}
                  label={I18n.t('Open on Save')}
                  onChange={setOpenOnSaveState}
                />
                <Button
                  disabled={submitting || !selectedFolder || !pedigreeName || initializing}
                  {...getTestAttributes({ id: 'btn-copy-ped-save' })}
                  onClick={handleSubmit}
                >
                  {I18n.t("Save")}
                </Button>
              </Flex>
              <Tree 
                getId={{
                  folder: getFolderId.folder,
                  collapseIcon: getFolderId.collapseIcon,
                }}
                className="PedigreesCreateShortcut__folders"
                items={folders}
                value={selectedFolder?.id}
                onChange={handleFolderChange} 
              />
            </>
          )
        }
      </ModalBody>
    </Modal>
  );
};
